import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Trash2, Plus, ArrowLeft, Image, Download, X, Pencil, Palette, Users, FileText, Save, ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = ({ onBack, onLogout }) => {
  const [activeTab, setActiveTab] = useState('content');
  const [partners, setPartners] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPartner, setNewPartner] = useState({ name: '', logo: null, preview: null });
  const [newSpecialty, setNewSpecialty] = useState({ title: '', description: '', image: null, preview: null });
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingSpecialty, setEditingSpecialty] = useState(null);
  const [editLogo, setEditLogo] = useState({ logo: null, preview: null });
  const [importing, setImporting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);

  // Resolve image URL - handles both local /api/uploads and storage /api/storage paths
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  const fetchPartners = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/partners`);
      if (response.ok) setPartners(await response.json());
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  }, []);

  const fetchSpecialties = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/specialties`);
      if (response.ok) setSpecialties(await response.json());
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content`);
      if (response.ok) setContent(await response.json());
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchPartners(), fetchSpecialties(), fetchContent()]).finally(() => setLoading(false));
  }, [fetchPartners, fetchSpecialties, fetchContent]);

  // Content handlers
  const handleContentChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveContent = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content)
      });
      if (response.ok) {
        toast.success('Contenu sauvegardé');
        fetchContent();
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Partner handlers
  const handleImportPartners = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/partners/import-defaults`, { method: 'POST' });
      const data = await response.json();
      if (data.imported > 0) {
        toast.success(`${data.imported} partenaires importés`);
        fetchPartners();
      } else {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    if (!newPartner.name.trim()) {
      toast.error('Veuillez entrer le nom du partenaire');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/partners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newPartner.name,
          logo_data: newPartner.logo?.data || null,
          logo_filename: newPartner.logo?.filename || null
        })
      });
      if (response.ok) {
        toast.success('Partenaire ajouté');
        setNewPartner({ name: '', logo: null, preview: null });
        fetchPartners();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeletePartner = async (partnerId, partnerName) => {
    if (!window.confirm(`Supprimer "${partnerName}" ?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/partners/${partnerId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Partenaire supprimé');
        setPartners(prev => prev.filter(p => p.id !== partnerId));
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdatePartnerLogo = async () => {
    if (!editingPartner || !editLogo.logo) return;
    try {
      const response = await fetch(`${API_URL}/api/partners/${editingPartner.id}/logo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logo_data: editLogo.logo.data,
          logo_filename: editLogo.logo.filename
        })
      });
      if (response.ok) {
        toast.success('Logo mis à jour');
        setEditingPartner(null);
        setEditLogo({ logo: null, preview: null });
        fetchPartners();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Specialty handlers
  const handleImportSpecialties = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/specialties/import-defaults`, { method: 'POST' });
      const data = await response.json();
      if (data.imported > 0) {
        toast.success(`${data.imported} spécialités importées`);
        fetchSpecialties();
      } else {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const handleAddSpecialty = async (e) => {
    e.preventDefault();
    if (!newSpecialty.title.trim() || !newSpecialty.description.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/specialties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newSpecialty.title,
          description: newSpecialty.description,
          image_data: newSpecialty.image?.data || null,
          image_filename: newSpecialty.image?.filename || null
        })
      });
      if (response.ok) {
        toast.success('Spécialité ajoutée');
        setNewSpecialty({ title: '', description: '', image: null, preview: null });
        fetchSpecialties();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteSpecialty = async (specialtyId, title) => {
    if (!window.confirm(`Supprimer "${title}" ?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/specialties/${specialtyId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Spécialité supprimée');
        setSpecialties(prev => prev.filter(s => s.id !== specialtyId));
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateSpecialty = async () => {
    if (!editingSpecialty) return;
    
    try {
      await fetch(`${API_URL}/api/specialties/${editingSpecialty.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingSpecialty.title,
          description: editingSpecialty.description
        })
      });
      
      toast.success('Spécialité mise à jour');
      setEditingSpecialty(null);
      fetchSpecialties();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAddImageToSpecialty = async (specialtyId, file) => {
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10 Mo)');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploadingImage(specialtyId);

    // Compress image if > 2MB
    const processFile = (inputFile) => {
      return new Promise((resolve) => {
        if (inputFile.size <= 2 * 1024 * 1024) {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(inputFile);
          return;
        }
        const img = new window.Image();
        const url = URL.createObjectURL(inputFile);
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          const maxDim = 1920;
          if (width > maxDim || height > maxDim) {
            if (width > height) { height = (height / width) * maxDim; width = maxDim; }
            else { width = (width / height) * maxDim; height = maxDim; }
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
          URL.revokeObjectURL(url);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(inputFile);
        };
        img.src = url;
      });
    };

    try {
      const imageData = await processFile(file);
      const response = await fetch(`${API_URL}/api/specialties/${specialtyId}/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_data: imageData,
          image_filename: file.name
        })
      });
      
      if (response.ok) {
        toast.success('Photo ajoutée');
        fetchSpecialties();
        if (editingSpecialty?.id === specialtyId) {
          const updated = await fetch(`${API_URL}/api/specialties`);
          const data = await updated.json();
          const spec = data.find(s => s.id === specialtyId);
          if (spec) setEditingSpecialty(spec);
        }
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.detail || `Erreur serveur (${response.status})`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur réseau lors de l\'upload');
    } finally {
      setUploadingImage(null);
    }
  };

  const handleDeleteSpecialtyImage = async (specialtyId, imageIndex) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/specialties/${specialtyId}/images/${imageIndex}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Photo supprimée');
        fetchSpecialties();
        if (editingSpecialty?.id === specialtyId) {
          const updated = await fetch(`${API_URL}/api/specialties`);
          const data = await updated.json();
          const spec = data.find(s => s.id === specialtyId);
          if (spec) setEditingSpecialty(spec);
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // File handlers
  const handleFileChange = (e, setter) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setter(prev => ({
          ...prev,
          logo: { data: reader.result, filename: file.name },
          image: { data: reader.result, filename: file.name },
          preview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Image invalide (max 5 Mo)');
    }
  };

  const TextInput = ({ label, value, onChange, multiline = false }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-stone-600 mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-500"
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-500"
        />
      )}
    </div>
  );

  // Image Gallery component for editing specialty
  const ImageGallery = ({ specialty }) => {
    const images = specialty.images || [];
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-stone-600">
            Photos du carousel ({images.length}/5)
          </label>
          {images.length < 5 && (
            <label className={`flex items-center gap-2 px-3 py-1.5 text-white text-sm rounded-lg ${uploadingImage === specialty.id ? 'bg-blue-400 cursor-wait' : 'bg-blue-600 cursor-pointer hover:bg-blue-700'}`}>
              {uploadingImage === specialty.id ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Transfert en cours...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Ajouter une photo
                </>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploadingImage === specialty.id}
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleAddImageToSpecialty(specialty.id, e.target.files[0]);
                  }
                }}
              />
            </label>
          )}
        </div>
        
        {images.length === 0 ? (
          <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center">
            <Image size={48} className="mx-auto mb-4 text-stone-300" />
            <p className="text-stone-500">Aucune photo</p>
            <p className="text-stone-400 text-sm">Ajoutez jusqu'à 5 photos pour le carousel</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {images.map((img, idx) => (
              <div key={idx} className="relative group aspect-video bg-stone-100 rounded-lg overflow-hidden">
                <img
                  src={resolveImageUrl(img)}
                  alt={`Photo ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => handleDeleteSpecialtyImage(specialty.id, idx)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
                <span className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                  {idx + 1}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <div className="bg-stone-800 text-white py-6">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center justify-between mb-4">
            <button onClick={onBack} className="flex items-center gap-2 text-stone-300 hover:text-white">
              <ArrowLeft size={20} /> Retour au site
            </button>
            <button 
              onClick={onLogout} 
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <LogOut size={18} /> Déconnexion
            </button>
          </div>
          <h1 className="text-2xl font-serif">Administration</h1>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4 flex-wrap">
            <button
              onClick={() => setActiveTab('content')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'content' ? 'bg-white text-stone-800' : 'bg-stone-700 text-white hover:bg-stone-600'
              }`}
            >
              <FileText size={18} /> Contenu
            </button>
            <button
              onClick={() => setActiveTab('specialties')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'specialties' ? 'bg-white text-stone-800' : 'bg-stone-700 text-white hover:bg-stone-600'
              }`}
            >
              <Palette size={18} /> Spécialités
            </button>
            <button
              onClick={() => setActiveTab('partners')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'partners' ? 'bg-white text-stone-800' : 'bg-stone-700 text-white hover:bg-stone-600'
              }`}
            >
              <Users size={18} /> Partenaires
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12 text-stone-500">Chargement...</div>
        ) : (
          <>
            {/* Content Tab */}
            {activeTab === 'content' && content && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-medium text-stone-800 mb-4">Section Hero</h2>
                  <TextInput label="Slogan principal" value={content.tagline} onChange={(v) => handleContentChange('tagline', v)} multiline />
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-medium text-stone-800 mb-4">Section Histoire</h2>
                  <TextInput label="Sous-titre" value={content.biography_subtitle} onChange={(v) => handleContentChange('biography_subtitle', v)} />
                  <TextInput label="Paragraphe 1" value={content.biography_paragraph1} onChange={(v) => handleContentChange('biography_paragraph1', v)} multiline />
                  <TextInput label="Paragraphe 2" value={content.biography_paragraph2} onChange={(v) => handleContentChange('biography_paragraph2', v)} multiline />
                  <TextInput label="Paragraphe 3" value={content.biography_paragraph3} onChange={(v) => handleContentChange('biography_paragraph3', v)} multiline />
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-medium text-stone-800 mb-4">Chiffres clés</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <TextInput label="Chiffre 1 - Valeur" value={content.key_figure1_value} onChange={(v) => handleContentChange('key_figure1_value', v)} />
                      <TextInput label="Chiffre 1 - Label" value={content.key_figure1_label} onChange={(v) => handleContentChange('key_figure1_label', v)} />
                    </div>
                    <div>
                      <TextInput label="Chiffre 2 - Valeur" value={content.key_figure2_value} onChange={(v) => handleContentChange('key_figure2_value', v)} />
                      <TextInput label="Chiffre 2 - Label" value={content.key_figure2_label} onChange={(v) => handleContentChange('key_figure2_label', v)} />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-medium text-stone-800 mb-4">Section Rénovation</h2>
                  <TextInput label="Titre" value={content.renovation_title} onChange={(v) => handleContentChange('renovation_title', v)} />
                  <TextInput label="Description" value={content.renovation_description} onChange={(v) => handleContentChange('renovation_description', v)} multiline />
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-medium text-stone-800 mb-4">Coordonnées</h2>
                  <TextInput label="Adresse" value={content.address} onChange={(v) => handleContentChange('address', v)} />
                  <TextInput label="Téléphone" value={content.phone} onChange={(v) => handleContentChange('phone', v)} />
                  <TextInput label="Email" value={content.email} onChange={(v) => handleContentChange('email', v)} />
                </div>

                <div className="flex justify-end">
                  <button onClick={handleSaveContent} disabled={saving} className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                    <Save size={20} />
                    {saving ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
                  </button>
                </div>
              </div>
            )}

            {/* Partners Tab */}
            {activeTab === 'partners' && (
              <>
                <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                  <h2 className="text-lg font-medium text-stone-800 mb-4 flex items-center gap-2">
                    <Plus size={20} /> Ajouter un partenaire
                  </h2>
                  <form onSubmit={handleAddPartner} className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm text-stone-600 mb-1">Nom</label>
                      <input type="text" value={newPartner.name} onChange={(e) => setNewPartner(prev => ({ ...prev, name: e.target.value }))} placeholder="Ex: Bouygues Immobilier" className="w-full px-4 py-2 border border-stone-300 rounded-lg" />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm text-stone-600 mb-1">Logo (optionnel)</label>
                      <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-stone-400">
                        <Upload size={18} className="text-stone-400" />
                        <span className="text-stone-500 text-sm truncate">{newPartner.logo?.filename || 'Choisir un fichier'}</span>
                        <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setNewPartner)} className="hidden" />
                      </label>
                    </div>
                    <button type="submit" className="px-6 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-700">Ajouter</button>
                  </form>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium text-stone-800">Partenaires ({partners.length})</h2>
                    {partners.length === 0 && (
                      <button onClick={handleImportPartners} disabled={importing} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        <Download size={16} /> {importing ? 'Import...' : 'Importer par défaut'}
                      </button>
                    )}
                  </div>
                  
                  {partners.length === 0 ? (
                    <div className="text-center py-12 text-stone-500">
                      <Image size={48} className="mx-auto mb-4 opacity-30" />
                      <p>Aucun partenaire</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {partners.map((partner) => (
                        <div key={partner.id} className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg border border-stone-200 hover:bg-stone-100">
                          <div onClick={() => { setEditingPartner(partner); setEditLogo({ logo: null, preview: null }); }} className="flex items-center gap-4 flex-1 cursor-pointer group">
                            <div className="w-16 h-12 bg-white rounded flex items-center justify-center overflow-hidden border border-stone-200 group-hover:border-blue-400 relative">
                              {partner.logo_url ? (
                                <img src={resolveImageUrl(partner.logo_url)} alt={partner.name} className="max-w-full max-h-full object-contain" />
                              ) : (
                                <span className="text-stone-400 font-bold text-xs">{partner.name.substring(0, 2).toUpperCase()}</span>
                              )}
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center">
                                <Pencil size={14} className="text-white" />
                              </div>
                            </div>
                            <span className="font-medium text-stone-700">{partner.name}</span>
                          </div>
                          <button onClick={() => handleDeletePartner(partner.id, partner.name)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Specialties Tab */}
            {activeTab === 'specialties' && (
              <>
                <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                  <h2 className="text-lg font-medium text-stone-800 mb-4 flex items-center gap-2">
                    <Plus size={20} /> Ajouter une spécialité
                  </h2>
                  <form onSubmit={handleAddSpecialty} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-stone-600 mb-1">Titre</label>
                        <input type="text" value={newSpecialty.title} onChange={(e) => setNewSpecialty(prev => ({ ...prev, title: e.target.value }))} placeholder="Ex: Carrelage" className="w-full px-4 py-2 border border-stone-300 rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-sm text-stone-600 mb-1">Image (optionnel)</label>
                        <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-stone-400">
                          <Upload size={18} className="text-stone-400" />
                          <span className="text-stone-500 text-sm truncate">{newSpecialty.image?.filename || 'Choisir une image'}</span>
                          <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setNewSpecialty)} className="hidden" />
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-stone-600 mb-1">Description</label>
                      <textarea value={newSpecialty.description} onChange={(e) => setNewSpecialty(prev => ({ ...prev, description: e.target.value }))} placeholder="Description de la spécialité..." rows={2} className="w-full px-4 py-2 border border-stone-300 rounded-lg" />
                    </div>
                    <button type="submit" className="px-6 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-700">Ajouter</button>
                  </form>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium text-stone-800">Spécialités ({specialties.length})</h2>
                    {specialties.length === 0 && (
                      <button onClick={handleImportSpecialties} disabled={importing} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        <Download size={16} /> {importing ? 'Import...' : 'Importer par défaut'}
                      </button>
                    )}
                  </div>
                  
                  {specialties.length === 0 ? (
                    <div className="text-center py-12 text-stone-500">
                      <Image size={48} className="mx-auto mb-4 opacity-30" />
                      <p>Aucune spécialité</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {specialties.map((specialty) => (
                        <div key={specialty.id} className="relative overflow-hidden rounded-lg border border-stone-200 bg-stone-50">
                          <div className="aspect-video bg-stone-200 relative">
                            {specialty.images && specialty.images.length > 0 ? (
                              <img src={resolveImageUrl(specialty.images[0])} alt={specialty.title} className="w-full h-full object-cover" />
                            ) : specialty.image_url ? (
                              <img src={resolveImageUrl(specialty.image_url)} alt={specialty.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-stone-400">
                                <Image size={48} />
                              </div>
                            )}
                            {specialty.images && specialty.images.length > 1 && (
                              <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                {specialty.images.length} photos
                              </span>
                            )}
                          </div>
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <h3 className="font-medium text-stone-800">{specialty.title}</h3>
                                <p className="text-sm text-stone-500 mt-1 line-clamp-2">{specialty.description}</p>
                              </div>
                              <div className="flex gap-1">
                                <button onClick={() => setEditingSpecialty({...specialty})} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg" title="Modifier">
                                  <Pencil size={18} />
                                </button>
                                <button onClick={() => handleDeleteSpecialty(specialty.id, specialty.title)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Supprimer">
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Edit Partner Logo Modal */}
      {editingPartner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium text-stone-800">Modifier le logo</h3>
              <button onClick={() => setEditingPartner(null)} className="p-1 hover:bg-stone-100 rounded-lg">
                <X size={20} className="text-stone-500" />
              </button>
            </div>
            <p className="text-stone-600 mb-4">Partenaire : <strong>{editingPartner.name}</strong></p>
            <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-blue-400">
              {editLogo.preview ? (
                <img src={editLogo.preview} alt="Preview" className="max-w-full max-h-24 object-contain" />
              ) : (
                <>
                  <Upload size={32} className="text-stone-400" />
                  <span className="text-stone-500">Cliquez pour sélectionner</span>
                </>
              )}
              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setEditLogo)} className="hidden" />
            </label>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditingPartner(null)} className="flex-1 px-4 py-2 border border-stone-300 rounded-lg">Annuler</button>
              <button onClick={handleUpdatePartnerLogo} disabled={!editLogo.logo} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Specialty Modal */}
      {editingSpecialty && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium text-stone-800">Modifier la spécialité</h3>
              <button onClick={() => setEditingSpecialty(null)} className="p-1 hover:bg-stone-100 rounded-lg">
                <X size={20} className="text-stone-500" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Titre</label>
                <input type="text" value={editingSpecialty.title} onChange={(e) => setEditingSpecialty(prev => ({ ...prev, title: e.target.value }))} className="w-full px-4 py-2 border border-stone-300 rounded-lg" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Description</label>
                <textarea value={editingSpecialty.description} onChange={(e) => setEditingSpecialty(prev => ({ ...prev, description: e.target.value }))} rows={3} className="w-full px-4 py-2 border border-stone-300 rounded-lg" />
              </div>
              
              <ImageGallery specialty={editingSpecialty} />
            </div>
            
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditingSpecialty(null)} className="flex-1 px-4 py-2 border border-stone-300 rounded-lg">Annuler</button>
              <button onClick={handleUpdateSpecialty} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
